import {
  DownloadOutlined,
  LeftOutlined,
  RightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined
} from "@ant-design/icons"
import {
  Alert,
  Button,
  Drawer,
  Flex,
  Segmented,
  Skeleton,
  Typography,
  theme
} from "antd"
import React, { Suspense, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { FieldLayer, LazyPdfPages } from "../../../components/Esign/index.ts"
import { certificateName } from "../../../components/Esign/signing/types.ts"
import {
  getEnvelopeCertificate,
  getEnvelopeDocument,
  getEnvelopeFields,
  saveEnvelopeCertificate,
  saveEnvelopeDocument
} from "../api/esign.services.ts"
import { signerColor, tint } from "../helpers/signer-colors.ts"
import type { EnvelopeRecipient, SignatureField } from "../types/esign.types.ts"

const { Text } = Typography

type View = "document" | "certificate"

type Shown =
  | { state: "loading" }
  | { state: "failed" }
  | { state: "fieldsFailed" }
  | { state: "unreadable" }
  | { state: "ready"; bytes: Uint8Array; fields: SignatureField[] }

export interface EnvelopeDocumentDrawerProps {
  envelopeId: string
  documentName: string
  /** The line under the title: how far signing has got, in the caller's words. */
  subtitle?: string
  /** Whether the certificate of completion exists to be offered beside it. */
  withCertificate?: boolean
  /**
   * Draws the request's current fields over the pages, as placeholders, in
   * the editor's own renderer and colours. A preview without them would show
   * the bare document as though it were the prepared one, so a failure to
   * read them is a failure of the whole preview.
   */
  withFieldsFor?: EnvelopeRecipient[]
  onClose: () => void
}

const pdfName = (name: string) => (/\.pdf$/i.test(name) ? name : `${name}.pdf`)

/**
 * The document the server holds for this request, drawn in the app.
 *
 * Always the frozen copy behind `…/document/` — never the editable source it
 * was taken from, which may have moved on since. That is the whole point of
 * opening it here rather than linking to the file in Files.
 */
export function EnvelopeDocumentDrawer({
  envelopeId,
  documentName,
  subtitle,
  withCertificate = false,
  withFieldsFor,
  onClose
}: EnvelopeDocumentDrawerProps) {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const [view, setView] = useState<View>("document")
  const [shown, setShown] = useState<Shown>({ state: "loading" })
  const [attempt, setAttempt] = useState(0)
  const [saving, setSaving] = useState(false)
  const [zoom, setZoom] = useState(1)
  const [page, setPage] = useState(1)
  const [pageCount, setPageCount] = useState(0)
  const pagesRef = useRef<HTMLDivElement>(null)
  const generation = useRef(0)
  const goToPage = (next: number) => {
    const target = Math.min(Math.max(next, 1), Math.max(pageCount, 1))
    setPage(target)
    pagesRef.current
      ?.querySelector(`[data-page="${target}"]`)
      ?.scrollIntoView({ block: "start", behavior: "smooth" })
  }

  // Scrolling by hand moves the counter too: the page under the toolbar is the page.
  useEffect(() => {
    const body = pagesRef.current?.closest(".ant-drawer-body")
    if (!body || !pageCount) return
    const follow = () => {
      const edge = body.getBoundingClientRect().top + 80
      const pages =
        pagesRef.current?.querySelectorAll<HTMLElement>("[data-page]")
      let current = 1
      pages?.forEach((element) => {
        if (element.getBoundingClientRect().top <= edge)
          current = Number(element.dataset.page) || current
      })
      setPage(current)
    }
    body.addEventListener("scroll", follow, { passive: true })
    return () => body.removeEventListener("scroll", follow)
  }, [pageCount])

  // Read for its presence only: a new array of the same signers must not refetch.
  const showsFields = Boolean(withFieldsFor)

  useEffect(() => {
    const mine = ++generation.current
    setShown({ state: "loading" })
    const load =
      view === "certificate" ? getEnvelopeCertificate : getEnvelopeDocument
    const withFields = showsFields && view === "document"
    void Promise.all([
      load(envelopeId),
      withFields ? getEnvelopeFields(envelopeId) : Promise.resolve(null)
    ])
      .then(([result, fields]) => {
        if (mine !== generation.current) return
        const fieldsOk =
          !withFields || (fields?.ok && Array.isArray(fields.data))
        setShown(
          !result.ok || !result.data
            ? { state: "failed" }
            : // Without its fields the preview would show a document nobody signs.
              !fieldsOk
              ? { state: "fieldsFailed" }
              : {
                  state: "ready",
                  bytes: result.data,
                  fields: fields?.data ?? []
                }
        )
      })
      .catch(() => {
        if (mine === generation.current) setShown({ state: "failed" })
      })
    return () => {
      generation.current += 1
    }
  }, [envelopeId, view, attempt, showsFields])

  const download = async () => {
    setSaving(true)
    const name = pdfName(documentName)
    if (view === "certificate") {
      await saveEnvelopeCertificate(envelopeId, certificateName(name))
    } else await saveEnvelopeDocument(envelopeId, name)
    setSaving(false)
  }

  const retry = (
    <Button size={"small"} onClick={() => setAttempt((count) => count + 1)}>
      {t("esign.tracking.viewer.retry")}
    </Button>
  )

  return (
    <Drawer
      open
      onClose={onClose}
      width={withFieldsFor ? "min(1180px, 100vw)" : "min(920px, 100vw)"}
      title={
        <Flex vertical>
          <Text strong ellipsis={{ tooltip: documentName }}>
            {documentName}
          </Text>
          {subtitle && (
            <Text type={"secondary"} className={"text-xs font-normal"}>
              {subtitle}
            </Text>
          )}
        </Flex>
      }
      extra={
        <Button
          icon={<DownloadOutlined />}
          loading={saving}
          onClick={() => void download()}
        >
          {t("esign.tracking.viewer.download")}
        </Button>
      }
    >
      <Flex vertical gap={"middle"}>
        {withCertificate && (
          <Segmented<View>
            value={view}
            onChange={setView}
            options={[
              { value: "document", label: t("esign.tracking.viewer.document") },
              {
                value: "certificate",
                label: t("esign.tracking.viewer.certificate")
              }
            ]}
          />
        )}
        {shown.state === "loading" && (
          <div aria-busy aria-label={t("esign.tracking.viewer.loading")}>
            <Skeleton active paragraph={{ rows: 10 }} />
          </div>
        )}
        {shown.state === "failed" && (
          <Alert
            type={"error"}
            showIcon
            message={t("esign.tracking.viewer.failed")}
            action={retry}
          />
        )}
        {shown.state === "fieldsFailed" && (
          <Alert
            type={"error"}
            showIcon
            message={t("esign.preview.fieldsFailed")}
            action={retry}
          />
        )}
        {shown.state === "unreadable" && (
          <Alert
            type={"warning"}
            showIcon
            message={t("esign.tracking.viewer.unreadable")}
            action={retry}
          />
        )}
        {shown.state === "ready" && (
          // Pinned over the pages; the white band above it covers the body's
          // own top padding, which a sticky box otherwise lets pages show through.
          <div className={"sticky -top-6 z-10 -mt-6 bg-white pt-6"}>
            <Flex
              align={"center"}
              justify={"space-between"}
              gap={"small"}
              wrap
              className={
                "rounded-md border border-solid border-slate-200 bg-white px-2 py-1"
              }
            >
              <Flex align={"center"} gap={4}>
                <Button
                  size={"small"}
                  type={"text"}
                  icon={<LeftOutlined />}
                  disabled={page <= 1}
                  aria-label={t("esign.editor.previousPage")}
                  onClick={() => goToPage(page - 1)}
                />
                <Text className={"text-xs"}>
                  {t("esign.editor.pageOf", {
                    page,
                    count: Math.max(pageCount, 1)
                  })}
                </Text>
                <Button
                  size={"small"}
                  type={"text"}
                  icon={<RightOutlined />}
                  disabled={page >= pageCount}
                  aria-label={t("esign.editor.nextPage")}
                  onClick={() => goToPage(page + 1)}
                />
              </Flex>
              <Flex align={"center"} gap={4}>
                <Button
                  size={"small"}
                  type={"text"}
                  icon={<ZoomOutOutlined />}
                  disabled={zoom <= 0.75}
                  aria-label={t("esign.editor.zoomOut")}
                  onClick={() =>
                    setZoom((value) => Math.max(0.75, value - 0.25))
                  }
                />
                <Button size={"small"} type={"text"} onClick={() => setZoom(1)}>
                  {zoom === 1
                    ? t("esign.editor.fitWidth")
                    : `${Math.round(zoom * 100)}%`}
                </Button>
                <Button
                  size={"small"}
                  type={"text"}
                  icon={<ZoomInOutlined />}
                  disabled={zoom >= 2}
                  aria-label={t("esign.editor.zoomIn")}
                  onClick={() => setZoom((value) => Math.min(2, value + 0.25))}
                />
              </Flex>
            </Flex>
          </div>
        )}
        {shown.state === "ready" && (
          <div
            ref={pagesRef}
            className={
              "overflow-auto rounded-md bg-slate-100 p-3 [&_[data-page]]:scroll-mt-16"
            }
          >
            <div style={{ width: `${zoom * 100}%` }}>
              <Suspense fallback={<Skeleton active paragraph={{ rows: 10 }} />}>
                <LazyPdfPages
                  key={`${envelopeId}-${view}-${attempt}`}
                  data={shown.bytes}
                  onError={() => setShown({ state: "unreadable" })}
                  onLoaded={setPageCount}
                  renderOverlay={
                    withFieldsFor && view === "document"
                      ? (pageNumber) => (
                          <FieldLayer
                            items={shown.fields.flatMap((field) =>
                              field.page === pageNumber &&
                              field.x !== null &&
                              field.y !== null &&
                              field.width !== null &&
                              field.height !== null
                                ? [
                                    {
                                      ...field,
                                      x: field.x,
                                      y: field.y,
                                      width: field.width,
                                      height: field.height
                                    }
                                  ]
                                : []
                            )}
                            render={(field, style) => {
                              const signers = withFieldsFor.filter(
                                (recipient) => recipient.role === "signer"
                              )
                              const index = signers.findIndex(
                                (signer) => signer.id === field.recipient
                              )
                              const color = signerColor(token, index)
                              const name = signers[index]?.name ?? ""
                              const type = t(
                                `esign.fieldTypes.${field.field_type}`
                              )
                              const label = `${type} · ${name}${field.required ? "" : ` · ${t("esign.preview.optional")}`}`
                              return (
                                <div
                                  key={field.id}
                                  title={label}
                                  aria-label={label}
                                  className={
                                    "flex items-center overflow-hidden rounded-sm px-1.5 text-xs"
                                  }
                                  style={{
                                    ...style,
                                    border: `1px ${field.required ? "solid" : "dashed"} ${color}`,
                                    background: tint(color),
                                    color
                                  }}
                                >
                                  <span className={"truncate font-medium"}>
                                    {label}
                                  </span>
                                </div>
                              )
                            }}
                          />
                        )
                      : undefined
                  }
                />
              </Suspense>
            </div>
          </div>
        )}
      </Flex>
    </Drawer>
  )
}
